import React, { useEffect, useMemo, useState } from "react";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Item from "../components/Item";
import KeywordBox from "../components/KeywordBox";
import AccountAppStats from "./AccountAppStats";
import AccountList from "./AccountList";
import AccountNotificationStats from "./AccountNotificationStats";
import AccountSignupChart from "./AccountSignupChart";
import { PAY_PLAN_APP_LABELS, PAY_PLAN_APPS_FOR_ADMIN } from "../../services/PayPlanUtil";

const ADMIN_ACCOUNT_SEARCH_KEY = "admin.account.searchOptions";
const PAGE_SIZE_OPTIONS = new Set([25, 50, 100]);
const DEFAULT_SORT = [{ field: "createdAt", sort: "desc" }];
const TEST_ACCOUNT_FILTERS = new Set(["all", "exclude", "include"]);
const VIEW_MODES = new Set(["list", "cards"]);
const APP_FILTERS = new Set(["", ...PAY_PLAN_APPS_FOR_ADMIN]);
const PAGE_TABS = new Set(["list", "stats", "notification"]);
const ACCOUNT_TAB_KEY = "admin.account.tab";

function migrateTestAccountFilter(parsed) {
    if (TEST_ACCOUNT_FILTERS.has(parsed?.testAccountFilter)) {
        return parsed.testAccountFilter;
    }
    if (parsed?.excludeTestAccount === false) {
        return "all";
    }
    return "exclude";
}

function loadSearchOptions() {
    try {
        const raw = localStorage.getItem(ADMIN_ACCOUNT_SEARCH_KEY);
        if (!raw) {
            return {
                keyword: "",
                testAccountFilter: "exclude",
                excludeStaff: false,
                excludeAppleStaff: false,
                page: 0,
                pageSize: 25,
                sort: DEFAULT_SORT,
                viewMode: "list",
                appCode: "",
            };
        }
        const parsed = JSON.parse(raw);
        const pageSize = PAGE_SIZE_OPTIONS.has(parsed?.pageSize) ? parsed.pageSize : 25;
        const page = Number.isFinite(parsed?.page) && parsed.page >= 0 ? parsed.page : 0;
        const sort = Array.isArray(parsed?.sort) && parsed.sort.length > 0
            ? parsed.sort
            : DEFAULT_SORT;
        return {
            keyword: typeof parsed.keyword === "string" ? parsed.keyword : "",
            testAccountFilter: migrateTestAccountFilter(parsed),
            excludeStaff: typeof parsed.excludeStaff === "boolean" ? parsed.excludeStaff : false,
            excludeAppleStaff:
                typeof parsed.excludeAppleStaff === "boolean" ? parsed.excludeAppleStaff : false,
            page,
            pageSize,
            sort,
            viewMode: VIEW_MODES.has(parsed?.viewMode) ? parsed.viewMode : "list",
            appCode: APP_FILTERS.has(parsed?.appCode) ? parsed.appCode : "",
        };
    } catch {
        return {
            keyword: "",
            testAccountFilter: "exclude",
            excludeStaff: false,
            excludeAppleStaff: false,
            page: 0,
            pageSize: 25,
            sort: DEFAULT_SORT,
            viewMode: "list",
            appCode: "",
        };
    }
}

function saveSearchOptions(options) {
    try {
        localStorage.setItem(ADMIN_ACCOUNT_SEARCH_KEY, JSON.stringify(options));
    } catch {
        /* ignore quota / private mode */
    }
}

export default function AdminAccountMain() {
    const initialSearch = useMemo(() => loadSearchOptions(), []);
    const [keyword, setKeyword] = useState(initialSearch.keyword);
    /** all | exclude (non-testers) | include (testers only). */
    const [testAccountFilter, setTestAccountFilter] = useState(initialSearch.testAccountFilter);
    /** When checked, hide staff/company accounts (API: staff=false). */
    const [excludeStaff, setExcludeStaff] = useState(initialSearch.excludeStaff);
    /** When checked, hide Apple private-relay emails (@privaterelay.appleid.com). */
    const [excludeAppleStaff, setExcludeAppleStaff] = useState(initialSearch.excludeAppleStaff);
    const [pageModel, setPageModel] = useState({
        page: initialSearch.page,
        pageSize: initialSearch.pageSize,
    });
    const [sort, setSort] = useState(initialSearch.sort);
    const [viewMode, setViewMode] = useState(initialSearch.viewMode);
    const [appCode, setAppCode] = useState(initialSearch.appCode);
    const [pageTab, setPageTab] = useState(() => {
        try {
            const stored = localStorage.getItem(ACCOUNT_TAB_KEY);
            return PAGE_TABS.has(stored) ? stored : "list";
        } catch {
            return "list";
        }
    });

    useEffect(() => {
        saveSearchOptions({
            keyword,
            testAccountFilter,
            excludeStaff,
            excludeAppleStaff,
            page: pageModel.page,
            pageSize: pageModel.pageSize,
            sort,
            viewMode,
            appCode,
        });
    }, [keyword, testAccountFilter, excludeStaff, excludeAppleStaff, pageModel, sort, viewMode, appCode]);

    const handleKeywordChange = (value) => {
        setKeyword(value);
        setPageModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    };

    const handleTestAccountFilterChange = (value) => {
        setTestAccountFilter(value);
        setPageModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    };

    const handleExcludeStaffChange = (checked) => {
        setExcludeStaff(checked);
        setPageModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    };

    const handleExcludeAppleStaffChange = (checked) => {
        setExcludeAppleStaff(checked);
        setPageModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    };

    const handleAppCodeChange = (value) => {
        setAppCode(value);
        setPageModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tabs
                    value={pageTab}
                    onChange={(_, value) => {
                        if (!PAGE_TABS.has(value)) return;
                        setPageTab(value);
                        try {
                            localStorage.setItem(ACCOUNT_TAB_KEY, value);
                        } catch {
                            /* ignore */
                        }
                    }}
                    aria-label="Account sections"
                >
                    <Tab label="List" value="list" />
                    <Tab label="Stats" value="stats" />
                    <Tab label="Notification" value="notification" />
                </Tabs>
            </Box>
            {pageTab === "stats" ? (
                <Box>
                    <Box sx={{ mb: 3 }}>
                        <AccountSignupChart
                            testAccountFilter="exclude"
                            excludeStaffUser
                            excludeAppleStaff
                        />
                    </Box>
                    <AccountAppStats
                        testAccountFilter="exclude"
                        excludeStaffUser
                        excludeAppleStaff
                    />
                </Box>
            ) : pageTab === "notification" ? (
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                        <Item
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 1,
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                                <KeywordBox callback={handleKeywordChange} initialValue={initialSearch.keyword} />
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <InputLabel id="admin-account-notification-test-filter-label">Test accounts</InputLabel>
                                    <Select
                                        labelId="admin-account-notification-test-filter-label"
                                        label="Test accounts"
                                        value={testAccountFilter}
                                        onChange={(e) => handleTestAccountFilterChange(e.target.value)}
                                    >
                                        <MenuItem value="all">All</MenuItem>
                                        <MenuItem value="exclude">Exclude tester</MenuItem>
                                        <MenuItem value="include">Include tester</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={excludeStaff}
                                            onChange={(e) => handleExcludeStaffChange(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label="Exclude staff"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={excludeAppleStaff}
                                            onChange={(e) => handleExcludeAppleStaffChange(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label="Exclude Apple staff"
                                />
                            </Box>
                        </Item>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Item>
                            <AccountNotificationStats
                                keyword={keyword}
                                testAccountFilter={testAccountFilter}
                                excludeStaffUser={excludeStaff}
                                excludeAppleStaff={excludeAppleStaff}
                            />
                        </Item>
                    </Grid>
                </Grid>
            ) : (
            <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <Item
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={viewMode}
                                onChange={(event, value) => {
                                    if (value) setViewMode(value);
                                }}
                                aria-label="Account view"
                            >
                                <ToggleButton value="list" aria-label="List view" title="List" sx={{ px: 1, minWidth: 44, minHeight: 40 }}>
                                    <ViewListIcon fontSize="small" />
                                </ToggleButton>
                                <ToggleButton value="cards" aria-label="Card view" title="Cards" sx={{ px: 1, minWidth: 44, minHeight: 40 }}>
                                    <ViewModuleIcon fontSize="small" />
                                </ToggleButton>
                            </ToggleButtonGroup>
                            <KeywordBox callback={handleKeywordChange} initialValue={initialSearch.keyword} />
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel id="admin-account-app-filter-label">App</InputLabel>
                                <Select
                                    labelId="admin-account-app-filter-label"
                                    label="App"
                                    value={appCode}
                                    onChange={(e) => handleAppCodeChange(e.target.value)}
                                >
                                    <MenuItem value="">All apps</MenuItem>
                                    {PAY_PLAN_APPS_FOR_ADMIN.map((code) => (
                                        <MenuItem key={code} value={code}>
                                            {PAY_PLAN_APP_LABELS[code] ?? code}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel id="admin-account-test-filter-label">Test accounts</InputLabel>
                                <Select
                                    labelId="admin-account-test-filter-label"
                                    label="Test accounts"
                                    value={testAccountFilter}
                                    onChange={(e) => handleTestAccountFilterChange(e.target.value)}
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="exclude">Exclude tester</MenuItem>
                                    <MenuItem value="include">Include tester</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={excludeStaff}
                                        onChange={(e) => handleExcludeStaffChange(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Exclude staff"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={excludeAppleStaff}
                                        onChange={(e) => handleExcludeAppleStaffChange(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Exclude Apple staff"
                            />
                        </Box>
                    </Item>
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <Item>
                        <AccountList
                            keyword={keyword}
                            testAccountFilter={testAccountFilter}
                            excludeStaffUser={excludeStaff}
                            excludeAppleStaff={excludeAppleStaff}
                            appCode={appCode}
                            variant={viewMode === "cards" ? "cards" : "table"}
                            pageModel={pageModel}
                            onPageModelChange={setPageModel}
                            sort={sort}
                            onSortChange={setSort}
                        />
                    </Item>
                </Grid>
            </Grid>
            )}
        </Box>
    );
}
